import React, { useState, useEffect } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Activity, CheckCircle2, XCircle, Loader2, RefreshCw, Upload, FileSpreadsheet, Mail, Gauge } from "lucide-react";
import { toast } from "sonner";

export default function Diagnostico() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fdData, setFdData] = useState(null);
  const [fdLoading, setFdLoading] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncJobs, setSyncJobs] = useState({ clientes: null, articulos: null });
  const [syncLaunching, setSyncLaunching] = useState(false);

  const loadSyncStatus = async () => {
    try {
      const { data } = await api.get("/facturadirecta/sync/status");
      setSyncStatus(data);
    } catch (e) { /* ignore */ }
  };

  const pollJob = async (jobId, kind) => {
    try {
      const { data } = await api.get(`/facturadirecta/sync/jobs/${jobId}`);
      setSyncJobs((s) => ({ ...s, [kind]: data }));
      if (data.status === "running") {
        setTimeout(() => pollJob(jobId, kind), 3000);
      } else {
        loadSyncStatus();
      }
    } catch (e) { /* ignore */ }
  };

  const launchSync = async () => {
    setSyncLaunching(true);
    try {
      const { data } = await api.post("/facturadirecta/sync/all", { async_job: true });
      setSyncJobs({
        clientes: { job_id: data.jobs.clientes, total: data.totals.clientes, status: "running", created: 0, updated: 0, errors: 0 },
        articulos: { job_id: data.jobs.articulos, total: data.totals.articulos, status: "running", created: 0, updated: 0, errors: 0 },
      });
      pollJob(data.jobs.clientes, "clientes");
      pollJob(data.jobs.articulos, "articulos");
    } catch (e) {
      alert("Error al lanzar sync: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSyncLaunching(false);
    }
  };

  const [redsysData, setRedsysData] = useState(null);
  const [redsysLoading, setRedsysLoading] = useState(false);

  // === Importación Excel histórico ===
  const [excelFile, setExcelFile] = useState(null);
  const [excelSheet, setExcelSheet] = useState("Materiales");
  const [excelResult, setExcelResult] = useState(null);
  const [excelLoading, setExcelLoading] = useState(false);

  // === Email test ===
  const [emailTo, setEmailTo] = useState("");
  const [emailIdentidad, setEmailIdentidad] = useState("notificaciones");
  const [emailResult, setEmailResult] = useState(null);
  const [emailLoading, setEmailLoading] = useState(false);

  // === Wialon infracciones ===
  const [wialonLookback, setWialonLookback] = useState(60);
  const [wialonLimit, setWialonLimit] = useState(110);
  const [wialonResult, setWialonResult] = useState(null);
  const [wialonLoading, setWialonLoading] = useState(false);

  const runWialonDetect = async () => {
    setWialonLoading(true);
    setWialonResult(null);
    try {
      const { data } = await api.post(
        `/wialon/infracciones/detect?lookback_min=${wialonLookback}&speed_limit_kmh=${wialonLimit}`
      );
      setWialonResult(data);
      if (data.infracciones_nuevas > 0) {
        toast.success(`${data.infracciones_nuevas} infracción(es) detectadas y guardadas`);
      } else if (data.infracciones_duplicadas > 0) {
        toast.info(`Sin nuevas · ${data.infracciones_duplicadas} ya existían`);
      } else {
        toast.success("Sin excesos detectados en el rango");
      }
    } catch (e) {
      setWialonResult({ error: e?.response?.data?.detail || e.message });
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setWialonLoading(false);
    }
  };

  const runEmailTest = async () => {
    if (!emailTo) { toast.error("Indica el email destinatario"); return; }
    setEmailLoading(true);
    setEmailResult(null);
    try {
      const { data } = await api.post(`/admin/email/test?to=${encodeURIComponent(emailTo)}&identidad=${emailIdentidad}`);
      setEmailResult(data);
      if (data.ok) toast.success("Email enviado · revisa la bandeja");
      else toast.error(data.error || "Error enviando");
    } catch (e) {
      setEmailResult({ ok: false, error: e?.response?.data?.detail || e.message });
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setEmailLoading(false); }
  };

  const runExcelImport = async (dryRun) => {
    if (!excelFile) { toast.error("Selecciona un archivo .xlsx"); return; }
    if (!dryRun && !window.confirm("⚠️ Importación REAL: se crearán clientes, espacios y peticiones en la base de datos. Asegúrate de haber hecho dry-run antes. ¿Continuar?")) return;
    setExcelLoading(true);
    setExcelResult(null);
    try {
      const fd = new FormData();
      fd.append("file", excelFile);
      const { data } = await api.post(
        `/admin/import-excel?sheet=${encodeURIComponent(excelSheet)}&dry_run=${dryRun}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 5 * 60 * 1000 }
      );
      setExcelResult(data);
      toast.success(dryRun
        ? `Dry-run OK · ${data.peticiones_procesadas} peticiones se importarían`
        : `Importación REAL OK · ${data.peticiones_procesadas} peticiones creadas`);
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      setExcelResult({ error: detail });
      toast.error(detail);
    } finally { setExcelLoading(false); }
  };

  const loadRedsys = async () => {
    setRedsysLoading(true);
    try {
      const { data } = await api.get("/integrations/redsys/status");
      setRedsysData(data);
    } catch (e) {
      setRedsysData({ error: e?.response?.data?.detail || e.message });
    } finally { setRedsysLoading(false); }
  };

  useEffect(() => { loadSyncStatus(); }, []);

  const run = async () => {
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const { data } = await api.get("/diag/pdf-stack");
      setData(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Error");
    } finally {
      setLoading(false);
    }
  };

  const runFD = async () => {
    setFdLoading(true);
    setFdData(null);
    try {
      const { data } = await api.get("/facturadirecta/health");
      setFdData(data);
    } catch (e) {
      setFdData({ error: e?.response?.data?.detail || e.message });
    } finally {
      setFdLoading(false);
    }
  };

  const Pill = ({ ok, label }) => (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
      ok ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40"
         : "bg-rose-500/20 text-rose-300 border border-rose-500/40"
    }`}>
      {ok ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
      {label || (ok ? "OK" : "FALLO")}
    </span>
  );

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="diagnostico-page">
      <PageHeader title="Diagnóstico técnico" subtitle="Verifica el pipeline de PDF + Cloudinary" icon={Activity} />

      <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-5">
        <p className="text-sm text-zinc-400 mb-4">
          Esta página comprueba en orden: <b className="text-zinc-100">WeasyPrint</b> (renderizado PDF) →{" "}
          <b className="text-zinc-100">Cloudinary config</b> →{" "}
          <b className="text-zinc-100">Cloudinary usage</b> →{" "}
          <b className="text-zinc-100">Subida de PDF de prueba</b> → cleanup.
          Si algún paso falla, aquí se ve EXACTAMENTE dónde y con qué error.
        </p>
        <Button onClick={run} disabled={loading} data-testid="btn-ejecutar-diag"
          className="bg-amber-500 hover:bg-amber-600 text-zinc-900 font-bold">
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Activity className="w-4 h-4 mr-2" />}
          Ejecutar diagnóstico PDF
        </Button>
        <Button onClick={runFD} disabled={fdLoading} data-testid="btn-test-fd"
          className="ml-2 bg-blue-500 hover:bg-blue-600 text-white font-bold">
          {fdLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Activity className="w-4 h-4 mr-2" />}
          Verificar FacturaDirecta
        </Button>
        <Button onClick={loadRedsys} disabled={redsysLoading} data-testid="btn-test-redsys"
          className="ml-2 bg-purple-500 hover:bg-purple-600 text-white font-bold">
          {redsysLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Activity className="w-4 h-4 mr-2" />}
          Verificar TPV Redsys
        </Button>
      </div>

      {/* === Redsys TPV status === */}
      {redsysData && (
        <div className={`rounded-xl p-5 border-2 ${redsysData?.enabled ? "bg-emerald-500/10 border-emerald-500/40" : "bg-amber-500/10 border-amber-500/40"}`} data-testid="redsys-result">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-zinc-100">Pasarela Redsys (TPV)</h3>
            {redsysData?.enabled
              ? <span className="text-[10px] uppercase bg-emerald-500/30 text-emerald-200 px-2 py-1 rounded">{redsysData.modo === "produccion" ? "PRODUCCIÓN" : "TEST"}</span>
              : <span className="text-[10px] uppercase bg-amber-500/30 text-amber-200 px-2 py-1 rounded">MOCK · falta config</span>}
          </div>
          {redsysData?.error ? (
            <div className="text-xs text-rose-300 font-mono break-all">{redsysData.error}</div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                <Row label="Merchant code (FUC)" value={redsysData.merchant_code || "— (REDSYS_MERCHANT_CODE)"} warn={!redsysData.merchant_code} />
                <Row label="Terminal" value={redsysData.terminal || "— (REDSYS_TERMINAL)"} warn={!redsysData.terminal} />
                <Row label="Clave SHA-256" value={redsysData.secret_key_set
                  ? (redsysData.secret_key_valid_base64
                      ? `✓ Base64 válida · ${redsysData.secret_key_len_bytes} bytes`
                      : "✗ NO es base64 válido")
                  : "— (REDSYS_SECRET_KEY)"} warn={!redsysData.secret_key_set || !redsysData.secret_key_valid_base64} />
                <Row label="Endpoint" value={redsysData.endpoint} />
                <Row label="MerchantURL (callback)" value={redsysData.merchant_url || "—"} warn={!redsysData.merchant_url} />
                <Row label="URL OK" value={redsysData.url_ok || "—"} />
                <Row label="URL KO" value={redsysData.url_ko || "—"} />
              </div>
              <div className="mt-3 space-y-1">
                {(redsysData.checks || []).map((c, i) => (
                  <div key={i} className={`text-xs px-2 py-1 rounded ${
                    c.level === "ok" ? "bg-emerald-500/15 text-emerald-200" :
                    c.level === "warning" ? "bg-amber-500/15 text-amber-200" :
                    "bg-rose-500/15 text-rose-200"
                  }`}>
                    {c.level === "ok" ? "✓" : c.level === "warning" ? "⚠" : "✗"} {c.msg}
                  </div>
                ))}
              </div>
              {redsysData.test_cards && (
                <div className="mt-3 text-[11px] text-zinc-400 bg-zinc-900/60 border border-zinc-800 rounded p-2 font-mono">
                  <div className="text-zinc-500 uppercase tracking-wider mb-1">Tarjetas de prueba Redsys</div>
                  <div>OK · {redsysData.test_cards.ok_visa}</div>
                  <div>KO · {redsysData.test_cards.ko_generic}</div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {fdData && (
        <div className={`rounded-xl p-5 border-2 ${fdData?.connection?.ok ? "bg-emerald-500/10 border-emerald-500/40" : "bg-rose-500/10 border-rose-500/40"}`} data-testid="fd-result">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-zinc-100">FacturaDirecta</h3>
            {fdData?.connection?.ok ? <CheckCircle2 className="w-5 h-5 text-emerald-400" /> : <XCircle className="w-5 h-5 text-rose-400" />}
          </div>
          {fdData?.connection?.ok ? (
            <div className="text-xs text-zinc-300 space-y-1">
              <div>✅ Conectado en <b className="text-emerald-300">{fdData.connection.ms}ms</b></div>
              <div>Empresa: <span className="font-mono text-[10px]">{fdData.company_id}</span></div>
              <div>Entorno: <b>{fdData.env}</b> · prefix <span className="font-mono">{fdData.prefix}</span></div>
              <div>Contactos visibles: <b>{fdData.connection.contacts_count_sample}</b></div>
            </div>
          ) : (
            <div className="text-xs text-rose-300 space-y-1">
              <div>❌ Status code: <b>{fdData?.connection?.status_code || "-"}</b></div>
              <div className="font-mono break-all">{JSON.stringify(fdData?.connection?.error || fdData?.error || "Error desconocido")}</div>
            </div>
          )}
        </div>
      )}

      {/* === Email Resend test === */}
      <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-5" data-testid="email-test-panel">
        <div className="flex items-center gap-2 mb-3">
          <Mail className="w-5 h-5 text-cyan-400" />
          <h3 className="text-sm font-bold text-zinc-100">Test de email (Resend)</h3>
        </div>
        <p className="text-xs text-zinc-500 mb-4">
          Envía un email de prueba para validar que Resend + DNS están bien configurados.
          Si recibes el correo en la bandeja principal (no en spam), está OK.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
          <div className="md:col-span-2">
            <label className="text-[10px] uppercase tracking-wider text-zinc-500">Destinatario</label>
            <input value={emailTo} onChange={(e) => setEmailTo(e.target.value)} type="email"
              placeholder="fernando@rotulaciondnf.com" data-testid="email-to-input"
              className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-sm" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500">Identidad</label>
            <select value={emailIdentidad} onChange={(e) => setEmailIdentidad(e.target.value)} data-testid="email-identidad"
              className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-sm">
              <option value="notificaciones">notificaciones (interno)</option>
              <option value="presupuestos">presupuestos (cliente)</option>
              <option value="citas">citas (cliente)</option>
              <option value="default">default (administracion@)</option>
            </select>
          </div>
        </div>
        <Button onClick={runEmailTest} disabled={emailLoading || !emailTo} data-testid="btn-email-test"
          className="bg-cyan-500 hover:bg-cyan-600 text-zinc-900 font-bold">
          {emailLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
          Enviar email de prueba
        </Button>
        {emailResult && (
          <div className={`mt-3 rounded p-3 text-xs border ${emailResult.ok ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-200" : "bg-rose-500/10 border-rose-500/30 text-rose-200"}`}
            data-testid="email-test-result">
            {emailResult.ok ? (
              <>✓ Email enviado · id={emailResult.id} · asunto: <b>{emailResult.subject}</b></>
            ) : (
              <>✗ {emailResult.error}
                {emailResult.error?.includes("not verified") && (
                  <div className="mt-2 text-rose-100/80 text-[11px]">
                    👉 Entra a <a href="https://resend.com/domains" target="_blank" rel="noreferrer" className="underline">resend.com/domains</a> y verifica el dominio que aparece en el error.
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* === Wialon · Detección manual de infracciones === */}
      <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-5" data-testid="wialon-infracciones-panel">
        <div className="flex items-center gap-2 mb-3">
          <Gauge className="w-5 h-5 text-orange-400" />
          <h3 className="text-sm font-bold text-zinc-100">Wialon · Infracciones de velocidad</h3>
        </div>
        <p className="text-xs text-zinc-500 mb-4">
          El sistema revisa <b>cada 15 minutos</b> los pings GPS de todos los vehículos vinculados a Wialon
          y registra automáticamente los excesos de velocidad sostenidos. Puedes lanzar una detección
          manual aquí para depurar o probar un rango concreto.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500">Ventana (minutos)</label>
            <input type="number" min="5" max="720" value={wialonLookback}
              onChange={(e) => setWialonLookback(parseInt(e.target.value || 60))}
              data-testid="wialon-lookback-input"
              className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-sm" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500">Límite km/h</label>
            <input type="number" min="30" max="200" value={wialonLimit}
              onChange={(e) => setWialonLimit(parseFloat(e.target.value || 110))}
              data-testid="wialon-limit-input"
              className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-sm" />
          </div>
          <div className="flex items-end">
            <Button onClick={runWialonDetect} disabled={wialonLoading} data-testid="btn-wialon-detect"
              className="w-full bg-orange-500 hover:bg-orange-600 text-zinc-900 font-bold">
              {wialonLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Gauge className="w-4 h-4 mr-2" />}
              Detectar ahora
            </Button>
          </div>
        </div>
        {wialonResult && !wialonResult.error && (
          <div className="rounded p-3 text-xs border bg-zinc-950 border-zinc-800 text-zinc-200 grid grid-cols-2 md:grid-cols-4 gap-3"
            data-testid="wialon-detect-result">
            <div><div className="text-zinc-500 text-[10px]">Vehículos</div><b className="text-base">{wialonResult.vehiculos_consultados}</b></div>
            <div><div className="text-zinc-500 text-[10px]">Episodios</div><b className="text-base">{wialonResult.episodios_detectados}</b></div>
            <div><div className="text-zinc-500 text-[10px]">Nuevas</div><b className="text-base text-emerald-400">{wialonResult.infracciones_nuevas}</b></div>
            <div><div className="text-zinc-500 text-[10px]">Duplicadas</div><b className="text-base text-zinc-400">{wialonResult.infracciones_duplicadas}</b></div>
            {wialonResult.errores && wialonResult.errores.length > 0 && (
              <div className="col-span-2 md:col-span-4 text-rose-300 text-[11px] mt-1">
                <b>Errores:</b> {wialonResult.errores.map(e => `${e.vehiculo_id.slice(0,6)}…: ${e.error}`).join(" · ")}
              </div>
            )}
          </div>
        )}
        {wialonResult?.error && (
          <div className="rounded p-3 text-xs border bg-rose-500/10 border-rose-500/30 text-rose-200">
            ✗ {wialonResult.error}
          </div>
        )}
      </div>

      {/* === Importación Excel histórico (Peticiones / Materiales) === */}
      <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-5" data-testid="excel-import-panel">
        <div className="flex items-center gap-2 mb-3">
          <FileSpreadsheet className="w-5 h-5 text-emerald-400" />
          <h3 className="text-sm font-bold text-zinc-100">Importar Excel histórico (peticiones)</h3>
        </div>
        <p className="text-xs text-zinc-500 mb-4">
          Sube el Excel con la hoja <b>Materiales</b>. Cada fila se convertirá en 1 Petición + 1 Línea + 1 Componente.
          Se crearán automáticamente clientes y espacios que no existan.
          <br /><b className="text-amber-300">Recomendado:</b> primero pulsa "Dry-run" para ver el resumen sin tocar la BD.
          Después "Importar de verdad".
          <br />Idempotente: filas con el mismo <code>legacy_id</code> que ya existan se saltan.
        </p>

        <div className="flex flex-col md:flex-row gap-3 mb-3">
          <label className="flex-1 cursor-pointer">
            <input type="file" accept=".xlsx,.xls"
              onChange={(e) => { setExcelFile(e.target.files?.[0] || null); setExcelResult(null); }}
              className="hidden"
              data-testid="excel-file-input" />
            <div className="bg-zinc-950 border-2 border-dashed border-zinc-700 hover:border-emerald-500/50 rounded-lg p-3 text-xs text-center text-zinc-400 hover:text-emerald-300 transition-colors">
              {excelFile
                ? <span><FileSpreadsheet className="w-4 h-4 inline mr-1" /> <b className="text-emerald-300">{excelFile.name}</b> · {(excelFile.size / 1024).toFixed(0)} KB</span>
                : <span><Upload className="w-4 h-4 inline mr-1" /> Click para seleccionar el archivo .xlsx</span>}
            </div>
          </label>
          <div className="md:w-48">
            <label className="text-[10px] uppercase tracking-wider text-zinc-500">Hoja</label>
            <input value={excelSheet} onChange={(e) => setExcelSheet(e.target.value)}
              data-testid="excel-sheet-input"
              className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-xs text-zinc-200" />
          </div>
        </div>

        <div className="flex gap-2 flex-wrap">
          <Button onClick={() => runExcelImport(true)} disabled={!excelFile || excelLoading}
            data-testid="btn-excel-dryrun"
            className="bg-amber-500 hover:bg-amber-600 text-zinc-900 font-bold">
            {excelLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Activity className="w-4 h-4 mr-2" />}
            Dry-run (simular)
          </Button>
          <Button onClick={() => runExcelImport(false)} disabled={!excelFile || excelLoading || !excelResult || excelResult.error}
            data-testid="btn-excel-import"
            className="bg-emerald-500 hover:bg-emerald-600 text-zinc-900 font-bold">
            {excelLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            Importar de verdad
          </Button>
        </div>

        {excelResult && (
          <div className={`mt-4 rounded-xl p-4 border ${excelResult.error ? "bg-rose-500/10 border-rose-500/40" : "bg-emerald-500/10 border-emerald-500/40"}`}
            data-testid="excel-import-result">
            {excelResult.error ? (
              <div className="text-xs text-rose-300 font-mono break-all">❌ {excelResult.error}</div>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                  <b className="text-sm">{excelResult.modo}</b>
                  <span className="text-[10px] uppercase bg-zinc-900 px-1.5 py-0.5 rounded">{excelResult.sheet}</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                  <Stat label="Filas total" value={excelResult.filas_total} />
                  <Stat label="Peticiones" value={excelResult.peticiones_procesadas} color="emerald" />
                  <Stat label="Clientes nuevos" value={excelResult.clientes_nuevos_creados} color="cyan" />
                  <Stat label="Espacios nuevos" value={excelResult.espacios_nuevos_creados} color="cyan" />
                  <Stat label="Sin cliente" value={excelResult.filas_saltadas_sin_cliente} color="amber" />
                  <Stat label="Ya existían" value={excelResult.filas_saltadas_ya_existian} color="zinc" />
                  <Stat label="Clientes sin mapeo" value={excelResult.clientes_sin_mapeo} color="amber" />
                  <Stat label="Errores" value={excelResult.errores} color="rose" />
                </div>
                {(excelResult.errores_muestra || []).length > 0 && (
                  <div className="mt-3 bg-rose-500/10 border border-rose-500/30 rounded p-2 text-[10px] font-mono text-rose-200 max-h-32 overflow-y-auto">
                    {excelResult.errores_muestra.map((e, i) => <div key={i}>· {e}</div>)}
                  </div>
                )}
                {(excelResult.log || []).length > 0 && (
                  <details className="mt-2">
                    <summary className="text-[10px] text-zinc-500 cursor-pointer hover:text-amber-300">Ver log detallado ({excelResult.log.length} líneas)</summary>
                    <div className="mt-1 bg-zinc-950 border border-zinc-800 rounded p-2 text-[10px] font-mono text-zinc-400 max-h-48 overflow-y-auto">
                      {excelResult.log.map((l, i) => <div key={i}>{l}</div>)}
                    </div>
                  </details>
                )}
              </>
            )}
          </div>
        )}
      </div>


      {/* === FacturaDirecta SYNC panel === */}
      <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-5" data-testid="fd-sync-panel">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-sm font-bold text-zinc-100">Sincronización FacturaDirecta</h3>
            <p className="text-xs text-zinc-500 mt-1">Empuja clientes (CIF, dirección, email…) y catálogo Logomedia hacia FD. Upsert idempotente por CIF / externalId.</p>
          </div>
          <Button onClick={loadSyncStatus} variant="outline" size="sm" data-testid="btn-refresh-sync-status" className="text-xs">
            <RefreshCw className="w-3 h-3 mr-1" /> Refrescar
          </Button>
        </div>

        {syncStatus && (
          <div className="grid grid-cols-2 gap-3 mb-4">
            <SyncCard
              title="Clientes"
              total={syncStatus.clientes.total}
              done={syncStatus.clientes.sincronizados}
              job={syncJobs.clientes}
              testid="sync-card-clientes"
            />
            <SyncCard
              title="Catálogo Logomedia"
              total={syncStatus.catalogo.total}
              done={syncStatus.catalogo.sincronizados}
              job={syncJobs.articulos}
              testid="sync-card-articulos"
            />
          </div>
        )}

        <Button
          onClick={launchSync}
          disabled={syncLaunching || syncJobs.clientes?.status === "running" || syncJobs.articulos?.status === "running"}
          data-testid="btn-launch-fd-sync"
          className="bg-emerald-500 hover:bg-emerald-600 text-zinc-900 font-bold"
        >
          {syncLaunching ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
          Sincronizar todo a FacturaDirecta
        </Button>
        <p className="text-[10px] text-zinc-500 mt-2">
          Tarda ~2-3 min. El proceso corre en background y sobrevive a recargas de página.
        </p>
      </div>

      {error && (
        <div className="bg-rose-500/10 border border-rose-500/40 rounded-xl p-4 text-rose-300 text-sm" data-testid="diag-error">
          <XCircle className="w-5 h-5 inline mr-2" />
          Error general: {error}
        </div>
      )}

      {data && (
        <div className="space-y-4" data-testid="diag-results">
          {/* ENV vars */}
          <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-zinc-100 mb-3">1. Env vars</h3>
            <div className="space-y-1.5 text-xs font-mono">
              <Row label="APP_PUBLIC_URL" value={data.env.APP_PUBLIC_URL}
                   warn={data.env.APP_PUBLIC_URL.includes("emergent")} />
              <Row label="CLOUDINARY_CLOUD_NAME" value={data.env.CLOUDINARY_CLOUD_NAME} />
              <Row label="CLOUDINARY_API_KEY" value={data.env.CLOUDINARY_API_KEY_present ? "configurado ✓" : "AUSENTE ✗"}
                   warn={!data.env.CLOUDINARY_API_KEY_present} />
              <Row label="CLOUDINARY_API_SECRET" value={data.env.CLOUDINARY_API_SECRET_present ? "configurado ✓" : "AUSENTE ✗"}
                   warn={!data.env.CLOUDINARY_API_SECRET_present} />
            </div>
          </div>

          {/* WeasyPrint */}
          <Card title="2. WeasyPrint (render PDF)" ok={data.weasyprint.ok}>
            {data.weasyprint.ok ? (
              <p className="text-xs text-zinc-400">Render OK en <b className="text-emerald-300">{data.weasyprint.ms}ms</b> · {data.weasyprint.bytes} bytes</p>
            ) : (
              <p className="text-xs text-rose-300 font-mono">{data.weasyprint.error}</p>
            )}
          </Card>

          {/* Cloudinary config */}
          <Card title="3. Cloudinary config" ok={data.cloudinary_config.ok}>
            {!data.cloudinary_config.ok && <p className="text-xs text-rose-300 font-mono">{data.cloudinary_config.error}</p>}
            {data.cloudinary_config.ok && <p className="text-xs text-emerald-300">Cliente Cloudinary inicializado correctamente.</p>}
          </Card>

          {/* Cloudinary usage */}
          <Card title="4. Cloudinary usage (cuota)" ok={data.cloudinary_usage.ok}>
            {data.cloudinary_usage.ok ? (
              <div className="text-xs text-zinc-400 space-y-1">
                <div>Plan: <b className="text-zinc-100">{data.cloudinary_usage.plan}</b></div>
                <div>Créditos usados: <b className="text-amber-300">{data.cloudinary_usage.credits_used_pct}%</b></div>
                <div>Espacio: <b className="text-zinc-100">{Math.round((data.cloudinary_usage.storage_bytes || 0) / 1024 / 1024)} MB</b></div>
              </div>
            ) : (
              <p className="text-xs text-rose-300 font-mono">{data.cloudinary_usage.error}</p>
            )}
          </Card>

          {/* Upload test */}
          <Card title="5. Subida PDF de prueba (CRÍTICO)" ok={data.cloudinary_upload_test.ok}>
            {data.cloudinary_upload_test.ok ? (
              <div className="text-xs text-zinc-400 space-y-1">
                <div>Upload completo en <b className="text-emerald-300">{data.cloudinary_upload_test.ms}ms</b></div>
                <div className="font-mono break-all text-[10px]">{data.cloudinary_upload_test.url}</div>
              </div>
            ) : (
              <div className="text-xs space-y-2">
                <p className="text-rose-300 font-mono break-all">{data.cloudinary_upload_test.error}</p>
                <p className="text-zinc-400">
                  → Esto es lo que está fallando en producción. Comparte este mensaje con Fer.
                </p>
              </div>
            )}
          </Card>

          {/* Cleanup */}
          <Card title="6. Cleanup test file" ok={data.cloudinary_cleanup.ok}>
            {!data.cloudinary_cleanup.ok && <p className="text-xs text-rose-300 font-mono">{data.cloudinary_cleanup.error}</p>}
            {data.cloudinary_cleanup.ok && <p className="text-xs text-emerald-300">Archivo de prueba borrado correctamente.</p>}
          </Card>

          {/* Veredicto */}
          <div className="bg-amber-500/10 border-2 border-amber-500/50 rounded-xl p-5">
            <h3 className="text-sm font-bold text-amber-200 mb-2">Veredicto</h3>
            {(() => {
              const allOk = data.weasyprint.ok && data.cloudinary_config.ok && data.cloudinary_usage.ok && data.cloudinary_upload_test.ok;
              if (allOk) {
                return <p className="text-xs text-emerald-300">
                  ✅ <b>Todo el pipeline funciona correctamente.</b> Si "Generar PDF" sigue fallando en presupuestos reales, el problema es el render con imágenes pesadas o timeout del proxy en cold-start. Solución: el endpoint ya tiene timeouts y devuelve errores claros — comparte el mensaje exacto que ves al pulsar el botón.
                </p>;
              }
              if (!data.cloudinary_upload_test.ok) {
                return <p className="text-xs text-rose-300">
                  ❌ <b>El upload a Cloudinary está fallando.</b> Causa probable: credenciales incorrectas en producción O cuota agotada O red bloqueada desde el pod hacia api.cloudinary.com. Mira el error exacto arriba y comparte con el equipo.
                </p>;
              }
              if (!data.weasyprint.ok) {
                return <p className="text-xs text-rose-300">
                  ❌ <b>WeasyPrint está fallando incluso con un HTML mínimo.</b> Esto indica un problema de instalación en el deploy. Reportar a Emergent Support.
                </p>;
              }
              return <p className="text-xs text-amber-300">Revisa los pasos individuales arriba.</p>;
            })()}
          </div>
        </div>
      )}
    </div>
  );
}

function Card({ title, ok, children }) {
  return (
    <div className={`bg-zinc-900/40 border rounded-xl p-5 ${ok ? "border-emerald-500/40" : "border-rose-500/40"}`}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-zinc-100">{title}</h3>
        {ok ? <CheckCircle2 className="w-5 h-5 text-emerald-400" /> : <XCircle className="w-5 h-5 text-rose-400" />}
      </div>
      {children}
    </div>
  );
}

function Row({ label, value, warn }) {
  return (
    <div className={`flex justify-between gap-2 ${warn ? "text-amber-300" : "text-zinc-300"}`}>
      <span className="text-zinc-500">{label}</span>
      <span className="break-all">{value}</span>
    </div>
  );
}

function Stat({ label, value, color }) {
  const colors = {
    emerald: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30",
    cyan: "text-cyan-300 bg-cyan-500/10 border-cyan-500/30",
    amber: "text-amber-300 bg-amber-500/10 border-amber-500/30",
    rose: "text-rose-300 bg-rose-500/10 border-rose-500/30",
    zinc: "text-zinc-300 bg-zinc-900 border-zinc-700",
  };
  const c = colors[color] || "text-zinc-200 bg-zinc-900 border-zinc-700";
  return (
    <div className={`border rounded-lg px-3 py-2 ${c}`}>
      <div className="text-[10px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-lg font-bold">{value ?? 0}</div>
    </div>
  );
}

function SyncCard({ title, total, done, job, testid }) {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const isRunning = job?.status === "running";
  const isDone = job?.status === "done";
  return (
    <div className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-zinc-300">{title}</span>
        {isRunning && <Loader2 className="w-3 h-3 text-amber-400 animate-spin" />}
        {isDone && <CheckCircle2 className="w-3 h-3 text-emerald-400" />}
      </div>
      <div className="text-2xl font-bold text-zinc-100">
        {done} <span className="text-sm text-zinc-500">/ {total}</span>
      </div>
      <div className="w-full bg-zinc-800 rounded-full h-1.5 mt-2 overflow-hidden">
        <div
          className={`h-full transition-all ${pct === 100 ? "bg-emerald-500" : "bg-amber-500"}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      {job && (
        <div className="flex gap-2 text-[10px] text-zinc-400 mt-2">
          <span>+{job.created || 0} nuevos</span>
          <span>~{job.updated || 0} actualizados</span>
          {job.errors > 0 && <span className="text-rose-400">! {job.errors} errores</span>}
        </div>
      )}
    </div>
  );
}
